import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { EstimateBalance, TransactionModel } from '@mmx/shared'
import { TranslateModule } from '@ngx-translate/core'

import { EstimateBalanceApiService } from '../../services/estimate-balance-api.service'

export interface BalanceRefundDialogData {
  balance: EstimateBalance
  transaction: TransactionModel
}

export interface BalanceRefundDialogResult {
  transaction: TransactionModel | null
  applied: boolean
  reason?: 'already-processed'
}

@Component({
  selector: 'mmx-balance-refund-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    TranslateModule,
  ],
  templateUrl: './balance-refund-dialog.component.html',
})
export class BalanceRefundDialogComponent {
  errorMessage: string | null = null
  isSubmitting = false

  readonly maxRefundAmount = this.data.transaction.amount

  readonly form = this.fb.group({
    amount: this.fb.control<number>(this.maxRefundAmount, [
      Validators.required,
      Validators.min(0.01),
      Validators.max(this.maxRefundAmount),
    ]),
  })

  private readonly destroyRef = inject(DestroyRef)

  constructor(
    private readonly fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: BalanceRefundDialogData,
    private readonly dialogRef: MatDialogRef<BalanceRefundDialogComponent, BalanceRefundDialogResult>,
    private readonly api: EstimateBalanceApiService,
  ) {}

  submit(): void {
    if (this.form.invalid || this.isSubmitting) {
      return
    }

    const value = this.form.getRawValue()
    this.errorMessage = null
    this.isSubmitting = true

    this.api.refundTransaction({
      transactionId: this.data.transaction.id,
      amount: value.amount!,
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        this.isSubmitting = false
        this.dialogRef.close({
          transaction: result.transaction,
          applied: result.applied,
          ...(result.applied === false && result.reason ? { reason: result.reason } : {}),
        })
      },
      error: (err: unknown) => {
        this.isSubmitting = false
        this.errorMessage = this.extractErrorMessage(err)
      },
    })
  }

  cancel(): void {
    this.dialogRef.close(undefined)
  }

  private extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object') {
      const e = err as { error?: { message?: string }; message?: string }
      return e.error?.message ?? e.message ?? 'Refund failed'
    }
    return 'Refund failed'
  }
}
